/**
 * MapLibre style definitions for Lyktan.
 *
 * Single style:
 *  - `vectorDarkStyle(apiKey)` — dark-themed vector basemap from Protomaps.
 *
 * The vector style is intentionally minimal: a few common Protomaps source
 * layers (`earth`, `water`, `landuse`, `roads`, `buildings`) styled with a
 * dark twilight palette to match Härden's design language. Hearths
 * (epic 04-04+) are added on top via dedicated layers.
 *
 * **Inga place-labels rendreras** i denna första iteration. Att rendera
 * symbol-lager kräver `glyphs`-fältet som måste peka på en font-PBF-host;
 * MapLibre demotiles uttryckligen markerade som icke-produktion av
 * MapLibre-organisationen, och Härden får inte göra externa anrop utöver
 * Protomaps (granskningsmönster #4 — okänd third-party för loggar/
 * spårning). Self-hosted glyphs eller Protomaps egen font-endpoint
 * tillkommer i en senare story tillsammans med place-labels-laget.
 */

import { ATTRIBUTION_VECTOR, buildProtomapsTileUrl } from './map';
import type { StyleSpecification } from 'maplibre-gl';

const COLOR_BACKGROUND = '#0e1014';
const COLOR_EARTH = '#15181e';
const COLOR_WATER = '#0a1722';
const COLOR_LANDUSE_PARK = '#142019';
const COLOR_ROAD_MINOR = '#262a33';
const COLOR_ROAD_MAJOR = '#3a3f4b';
const COLOR_BUILDING = '#1c2028';

export function vectorDarkStyle(apiKey: string): StyleSpecification {
  return {
    version: 8,
    sources: {
      protomaps: {
        type: 'vector',
        tiles: [buildProtomapsTileUrl(apiKey)],
        maxzoom: 15,
        attribution: ATTRIBUTION_VECTOR,
      },
    },
    layers: [
      {
        id: 'background',
        type: 'background',
        paint: { 'background-color': COLOR_BACKGROUND },
      },
      {
        id: 'earth',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'earth',
        paint: { 'fill-color': COLOR_EARTH },
      },
      {
        id: 'landuse-park',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'landuse',
        filter: ['in', 'kind', 'park', 'forest', 'wood', 'nature_reserve', 'cemetery'],
        paint: { 'fill-color': COLOR_LANDUSE_PARK },
      },
      {
        id: 'water',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'water',
        paint: { 'fill-color': COLOR_WATER },
      },
      {
        id: 'roads-minor',
        type: 'line',
        source: 'protomaps',
        'source-layer': 'roads',
        filter: ['!in', 'kind', 'highway', 'major_road'],
        minzoom: 12,
        paint: {
          'line-color': COLOR_ROAD_MINOR,
          'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.5, 18, 4],
        },
      },
      {
        id: 'roads-major',
        type: 'line',
        source: 'protomaps',
        'source-layer': 'roads',
        filter: ['in', 'kind', 'highway', 'major_road'],
        paint: {
          'line-color': COLOR_ROAD_MAJOR,
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 18, 6],
        },
      },
      {
        id: 'buildings',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'buildings',
        minzoom: 13,
        paint: { 'fill-color': COLOR_BUILDING, 'fill-opacity': 0.7 },
      },
      // place-labels-laget är medvetet utelämnat i denna iteration.
      // Symbol-rendering kräver `glyphs`-host; tidigare iteration använde
      // demotiles.maplibre.org vilket är (a) tredjeparts-CDN utöver de
      // tillåtna Protomaps (granskningsmönster #4) och (b) uttryckligen
      // icke-produktion enligt MapLibre. Adderas igen när self-hosted
      // font-PBF eller Protomaps-glyphs är på plats.
    ],
  };
}
